/**
 * Notification Service
 * Handles in-app notifications
 * 
 * @module services/notificationService
 */

import { supabase } from './supabase';
import { getCurrentUserId } from './authService';
import { ServiceResult, ok, err } from '../types/api/result';
import { AppError } from '../lib/errors/AppError';
import { logger } from '../lib/logger';

// =============================================================================
// Types
// =============================================================================

export interface Notification {
    id: string;
    userId: string;
    type: string;
    category?: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    title: string;
    message: string | null;
    data: Record<string, unknown>;
    isRead: boolean;
    createdAt: string;
}

export interface NotificationPreference {
    category: string;
    channel: 'in_app' | 'email' | 'push';
    enabled: boolean;
}

interface NotificationRow {
    id: string;
    user_id: string;
    type: string;
    category?: string;
    priority?: string;
    title: string;
    message: string | null;
    data: Record<string, unknown>;
    is_read: boolean;
    created_at: string;
}

// =============================================================================
// Helpers
// =============================================================================

function toNotification(row: NotificationRow): Notification {
    return {
        id: row.id,
        userId: row.user_id,
        type: row.type,
        category: row.category || 'general',
        priority: (row.priority as Notification['priority']) || 'medium',
        title: row.title,
        message: row.message,
        data: row.data || {},
        isRead: row.is_read,
        createdAt: row.created_at,
    };
}

// =============================================================================
// Service Operations
// =============================================================================

/**
 * Create a new notification
 */
export async function createNotification(
    userId: string,
    type: string,
    title: string,
    message?: string,
    data?: Record<string, unknown>
): Promise<ServiceResult<Notification>> {
    try {
        const { data: notification, error } = await supabase
            .from('notifications')
            .insert({
                user_id: userId,
                type,
                category: data?.category || 'general',
                priority: data?.priority || 'medium',
                title,
                message: message || null,
                data: data || {},
            })
            .select()
            .single();

        if (error) throw error;

        logger.info('[NotificationService] Notification created', { userId, type });
        return ok(toNotification(notification));

    } catch (error) {
        logger.error('[NotificationService] createNotification failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Get notifications for current user
 */
export async function getNotifications(
    limit: number = 20,
    unreadOnly: boolean = false
): Promise<ServiceResult<Notification[]>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) return ok([]);

        let query = supabase
            .from('notifications')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (unreadOnly) {
            query = query.eq('is_read', false);
        }

        const { data, error } = await query;

        if (error) throw error;

        return ok((data || []).map(toNotification));

    } catch (error) {
        logger.error('[NotificationService] getNotifications failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(): Promise<ServiceResult<number>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) return ok(0);

        const { count, error } = await supabase
            .from('notifications')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .eq('is_read', false);

        if (error) throw error;

        return ok(count || 0);

    } catch (error) {
        logger.error('[NotificationService] getUnreadCount failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Mark a notification as read
 */
export async function markAsRead(notificationId: string): Promise<ServiceResult<void>> {
    try {
        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('id', notificationId);

        if (error) throw error;

        return ok(undefined);

    } catch (error) {
        logger.error('[NotificationService] markAsRead failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<ServiceResult<void>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            return err(new AppError('User not authenticated', { code: 'UNAUTHENTICATED', statusCode: 401 }));
        }

        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('user_id', userId)
            .eq('is_read', false);

        if (error) throw error;

        return ok(undefined);

    } catch (error) {
        logger.error('[NotificationService] markAllAsRead failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Delete a notification
 */
export async function deleteNotification(notificationId: string): Promise<ServiceResult<void>> {
    try {
        const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('id', notificationId);

        if (error) throw error;

        return ok(undefined);

    } catch (error) {
        logger.error('[NotificationService] deleteNotification failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Subscribe to notifications (real-time)
 */
export function subscribeToNotifications(
    userId: string,
    onNotification: (notification: Notification) => void
): () => void {
    const channel = supabase
        .channel(`notifications:${userId}`)
        .on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'notifications',
                filter: `user_id=eq.${userId}`,
            },
            (payload) => {
                if (payload.new) {
                    onNotification(toNotification(payload.new as NotificationRow));
                }
            }
        )
        .subscribe();

    return () => {
        supabase.removeChannel(channel);
    };
}
/**
 * Get notification preferences for current user
 */
export async function getNotificationPreferences(): Promise<ServiceResult<NotificationPreference[]>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) return ok([]);

        const { data, error } = await supabase
            .from('notification_preferences')
            .select('category, channel, enabled')
            .eq('user_id', userId);

        if (error) throw error;

        return ok(data || []);
    } catch (error) {
        logger.error('[NotificationService] getNotificationPreferences failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Update notification preference
 */
export async function updateNotificationPreference(
    category: string,
    channel: 'in_app' | 'email' | 'push',
    enabled: boolean
): Promise<ServiceResult<void>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) return err(new AppError('Unauthenticated', { code: 'UNAUTHENTICATED', statusCode: 401 }));

        const { error } = await supabase
            .from('notification_preferences')
            .upsert({
                user_id: userId,
                category,
                channel,
                enabled,
                updated_at: new Date().toISOString()
            }, { onConflict: 'user_id,category,channel' });

        if (error) throw error;

        return ok(undefined);
    } catch (error) {
        logger.error('[NotificationService] updateNotificationPreference failed', error);
        return err(AppError.from(error));
    }
}
